import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { ChevronDown, Search, UserRoundSearch, X } from 'lucide-react';
import AccountStatement from '@/components/CuentaCorriente/AccountStatement.jsx';
import PageHeader from '@/components/PageHeader.jsx';
import { Q_CLIENTE, Q_CLIENTES } from '@/modules/administracion/clientes/clientes.gql.js';

const RECENT_CLIENTS_KEY = 'sinamon:cuenta-corriente:recent-clients';

const readRecentClientIds = () => {
  try {
    return JSON.parse(window.localStorage.getItem(RECENT_CLIENTS_KEY) || '[]');
  } catch {
    return [];
  }
};

function ClientResult({ client, active, onSelect }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onSelect(client.id)}
      className={`w-full rounded-xl border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${active ? 'border-[rgb(var(--brand))] bg-[rgba(var(--brand),.1)]' : 'border-transparent hover:bg-[rgba(var(--subpanel),.7)]'}`}
    >
      <span className="block truncate text-sm font-semibold">{client.razonSocial}</span>
      <span className="block truncate text-xs text-[rgba(var(--muted),.8)]">{[client.codigo && `Código ${client.codigo}`, client.cuit && `CUIT ${client.cuit}`].filter(Boolean).join(' · ') || `Cliente #${client.id}`}</span>
    </button>
  );
}

export default function AccountStatementsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedId = query.get('clienteId') || '';
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(!selectedId);
  const [activeIndex, setActiveIndex] = useState(0);
  const selectorRef = useRef(null);
  const searchRef = useRef(null);
  const [{ data, fetching, error }] = useQuery({
    query: Q_CLIENTES,
    variables: { page: 1, pageSize: 12, filter: filter ? { search: filter } : undefined },
  });
  const [{ data: selectedData, error: selectedError }] = useQuery({
    query: Q_CLIENTE,
    variables: { id: selectedId },
    pause: !selectedId,
  });
  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const closeOnOutsidePointer = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target) && selectedId) setSelectorOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [selectedId]);

  useEffect(() => {
    if (selectorOpen) window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [selectorOpen]);

  useEffect(() => {
    if (!selectedData?.cliente?.id) return;
    const ids = [String(selectedData.cliente.id), ...readRecentClientIds().filter((id) => id !== String(selectedData.cliente.id))].slice(0, 6);
    window.localStorage.setItem(RECENT_CLIENTS_KEY, JSON.stringify(ids));
  }, [selectedData]);

  const clients = useMemo(() => {
    const listed = data?.clientes?.edges?.map((edge) => edge.node) || [];
    const selected = selectedData?.cliente;
    const withSelected = selected && !listed.some((client) => String(client.id) === String(selected.id))
      ? [selected, ...listed]
      : listed;
    const recentIds = readRecentClientIds();
    if (search.trim() || !recentIds.length) return withSelected;
    const recentOrder = (client) => {
      const index = recentIds.indexOf(String(client.id));
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return [...withSelected].sort((a, b) => recentOrder(a) - recentOrder(b));
  }, [data, search, selectedData]);

  useEffect(() => setActiveIndex(0), [filter, selectorOpen]);

  const selectClient = (clientId) => {
    const next = new URLSearchParams(location.search);
    next.set('clienteId', clientId);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    setSearch('');
    setSelectorOpen(false);
  };

  const openSelector = () => {
    setSearch('');
    setSelectorOpen(true);
  };

  const handleSearchKeyDown = (event) => {
    if (!clients.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, clients.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectClient(clients[activeIndex]?.id);
    } else if (event.key === 'Escape' && selectedId) {
      setSelectorOpen(false);
    }
  };

  const initialFilters = {
    sucursalId: query.get('sucursalId') || '', desde: query.get('desde') || '', hasta: query.get('hasta') || '',
    tipo: query.get('tipo') || '', tipoDocumento: query.get('tipoDocumento') || '',
    soloPendientes: query.get('soloPendientes') || '', soloVencidos: query.get('soloVencidos') || '',
    incluirCancelados: query.get('incluirCancelados') || '', page: query.get('page') || 1, tab: query.get('tab') || 'movimientos',
  };

  return (
    <div className="account-statements-page w-full min-w-0 space-y-2 py-1">
      <PageHeader title="Resúmenes de cuenta" subtitle="Cuenta corriente cronológica y conciliable de clientes" />

      <section ref={selectorRef} className="relative z-20 rounded-2xl border border-[rgba(var(--border),.55)] bg-[rgba(var(--panel),.9)] p-3 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="relative block min-w-0 flex-1">
            <span className="sr-only">Buscar cliente por nombre, código o CUIT</span>
            <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[rgba(var(--muted),.8)]" />
            <input
              ref={searchRef}
              value={search}
              onFocus={() => setSelectorOpen(true)}
              onChange={(event) => { setSearch(event.target.value); setSelectorOpen(true); }}
              onKeyDown={handleSearchKeyDown}
              role="combobox"
              aria-expanded={selectorOpen}
              aria-controls="account-client-results"
              aria-activedescendant={clients[activeIndex] ? `account-client-${clients[activeIndex].id}` : undefined}
              placeholder="Buscar cliente por nombre, código o CUIT"
              className="h-11 w-full rounded-xl border bg-[rgb(var(--bg))] pl-10 pr-10 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
            {search ? <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-[rgba(var(--muted),.8)] hover:bg-[rgba(var(--brand),.1)]" aria-label="Limpiar búsqueda"><X size={15} /></button> : null}
          </label>
          {selectedId ? <button type="button" onClick={openSelector} className="inline-flex h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border px-3 text-sm font-semibold text-[rgb(var(--brand))]">Cambiar cliente<ChevronDown size={15} /></button> : null}
        </div>

        {selectorOpen ? <div id="account-client-results" role="listbox" className="mt-2 max-h-[min(420px,55dvh)] overflow-y-auto rounded-xl border border-[rgba(var(--border),.55)] bg-[rgb(var(--panel))] p-1.5 shadow-[0_16px_32px_rgba(var(--shadow),.18)]">
          {!search.trim() ? <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),.8)]">{readRecentClientIds().length ? 'Recientes' : 'Clientes'}</p> : null}
          {fetching && !clients.length ? <p className="p-4 text-center text-xs text-[rgba(var(--muted),.8)]">Buscando…</p> : null}
          {error ? <p className="m-1 rounded-xl border border-rose-500/30 p-3 text-xs text-rose-700">No se pudo buscar clientes.</p> : null}
          {selectedError ? <p className="m-1 rounded-xl border border-rose-500/30 p-3 text-xs text-rose-700">El cliente indicado en la URL no existe o no está disponible.</p> : null}
          {!fetching && !clients.length && !error ? <p className="p-4 text-center text-xs text-[rgba(var(--muted),.8)]">No se encontraron clientes.</p> : null}
          <div className="space-y-1">
            {clients.map((client, index) => <div id={`account-client-${client.id}`} key={client.id} className={index === activeIndex ? 'rounded-xl ring-2 ring-[rgb(var(--ring))] ring-offset-1 ring-offset-[rgb(var(--panel))]' : ''}><ClientResult client={client} active={String(client.id) === selectedId} onSelect={selectClient} /></div>)}
          </div>
        </div> : null}
      </section>

      {selectedId ? <AccountStatement key={selectedId} clienteId={selectedId} mode="full" initialFilters={initialFilters} /> : (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[rgba(var(--border),.65)] bg-[rgba(var(--panel),.45)] p-8 text-center">
          <UserRoundSearch size={38} className="text-[rgb(var(--brand))]" />
          <h2 className="mt-3 font-semibold">Seleccioná un cliente</h2>
          <p className="mt-1 max-w-md text-sm text-[rgba(var(--muted),.82)]">Usá el buscador de arriba para encontrarlo por nombre, código o CUIT.</p>
        </div>
      )}


    </div>
  );
}
